/**
 * Byte-stream line assembler for the serial console.
 *
 * WebUSB delivers bulk reads in arbitrary-sized chunks that split lines at any
 * offset ([ch340.md](../docs/ch340.md) bulk IN). This turns that stream into
 * whole console lines, holding a partial line across chunks and never emitting
 * it twice (REQ-CON-1, REQ-CON-2). Terminators are CR, LF, or CRLF; empty lines
 * are preserved. See [log-format.md §3](../docs/log-format.md) — the caller logs
 * each returned line verbatim and does not interpret it.
 *
 * Pure: no I/O, no DOM, no globals.
 */
export type LineAssembler = {
  /** Feed a chunk; return the lines it completed, terminators stripped. */
  push(chunk: Uint8Array): string[];
  /** The partial line held back, waiting for its terminator (may be empty). */
  pending(): string;
};

const CR = 0x0d;
const LF = 0x0a;

export function createLineAssembler(): LineAssembler {
  // Bytes of the current incomplete line, decoded only when a terminator lands
  // — so a multibyte UTF-8 character split across chunks is still whole by then.
  let buf: number[] = [];
  // The previous byte was a CR that ended a line; a following LF is the second
  // half of a CRLF and must be swallowed rather than starting an empty line.
  let pendingLf = false;
  const decoder = new TextDecoder();
  const take = (): string => {
    const line = decoder.decode(Uint8Array.from(buf));
    buf = [];
    return line;
  };

  return {
    push(chunk: Uint8Array): string[] {
      const lines: string[] = [];
      for (const b of chunk) {
        if (pendingLf) {
          pendingLf = false;
          if (b === LF) continue; // second half of a CRLF — already emitted
        }
        if (b === CR) {
          lines.push(take());
          pendingLf = true;
        } else if (b === LF) {
          lines.push(take());
        } else {
          buf.push(b);
        }
      }
      return lines;
    },
    pending(): string {
      return decoder.decode(Uint8Array.from(buf));
    },
  };
}
